// Index arithmetic behind the Field Cameras capture player: Play/Pause, the loop range and which
// neighbouring captures to fetch ahead of time. Captures are the page's oldest-first list. A
// range is stored by capture id ({start, end}, either may be null), so loading older captures,
// which prepends to the list, never moves it; resolveRange turns it into inclusive indices.

export const PLAYBACK_SPEEDS = [1, 2, 4]; // captures per second
export const DEFAULT_SPEED = 2;

const indexOfCapture = (captures, id) =>
  id ? captures.findIndex((capture) => capture.capture_id === id) : -1;

/**
 * Inclusive {start, end} indices of the loop range, or null when no range is set. An edge that
 * is not set (or no longer listed) is open: the range runs to the oldest or newest capture.
 */
export function resolveRange(captures, range) {
  if (!captures?.length || !range || (!range.start && !range.end)) return null;
  const start = indexOfCapture(captures, range.start);
  const end = indexOfCapture(captures, range.end);
  if (start < 0 && end < 0) return null;
  const from = start < 0 ? 0 : start;
  const to = end < 0 ? captures.length - 1 : end;
  return from <= to ? { start: from, end: to } : { start: to, end: from };
}

/**
 * Mark `id` as the range's start or end. A mark placed beyond the other edge clears that edge,
 * so marking a new start after the old end begins a new range instead of looping backwards.
 */
export function markRange(range, edge, id, captures) {
  const other = edge === "start" ? "end" : "start";
  const next = { start: null, end: null, ...range, [edge]: id };
  const at = indexOfCapture(captures, id);
  const otherAt = indexOfCapture(captures, next[other]);
  if (at >= 0 && otherAt >= 0 && (edge === "start" ? at > otherAt : at < otherAt))
    next[other] = null;
  return next;
}

/** Inclusive index bounds playback runs within: the range, else every loaded capture. */
export function playbackBounds(count, range) {
  if (!count) return null;
  return range || { start: 0, end: count - 1 };
}

/** Playback needs at least two captures inside its bounds. */
export function canPlay(count, range) {
  const bounds = playbackBounds(count, range);
  return !!bounds && bounds.end > bounds.start;
}

/**
 * Where Play starts: the current capture, unless it lies outside the range (then the range
 * start) or it is already the newest capture with no range (then the oldest, like a player
 * restarting a finished video).
 */
export function playStartIndex(index, count, range) {
  const bounds = playbackBounds(count, range);
  if (!bounds) return -1;
  if (index < bounds.start || index > bounds.end) return bounds.start;
  if (!range && index === bounds.end) return bounds.start;
  return index;
}

/**
 * The capture after `index` in play order: inside a range it loops back to the range start;
 * without one it stops (null) after the newest capture.
 */
export function nextPlaybackIndex(index, count, range) {
  const bounds = playbackBounds(count, range);
  if (!bounds) return null;
  if (range && (index >= bounds.end || index < bounds.start)) return bounds.start;
  if (index >= bounds.end) return null;
  return index + 1;
}

/**
 * Neighbours worth fetching before they are asked for, nearest first. While playing: the next
 * `ahead` captures in play order (wrapping inside a range). Otherwise: one either side, for
 * stepping.
 */
export function preloadIndices(index, count, { playing = false, range = null, ahead = 3 } = {}) {
  if (!(index >= 0 && index < count)) return [];
  if (!playing)
    return [index + 1, index - 1].filter((i) => i >= 0 && i < count);
  const out = [];
  let i = index;
  for (let n = 0; n < ahead; n += 1) {
    i = nextPlaybackIndex(i, count, range);
    if (i === null || i === index || out.includes(i)) break;
    out.push(i);
  }
  return out;
}

/**
 * True when `candidate` lies past `from` on the way to `to` (`to` included). While scrubbing, a
 * frame that finishes loading may be shown only if this holds, so the view moves towards the
 * slider and never jumps back to a frame the slider has already passed.
 */
export function liesBetween(from, candidate, to) {
  if (from < 0 || candidate < 0 || to < 0 || from === to) return false;
  return from < to
    ? candidate > from && candidate <= to
    : candidate < from && candidate >= to;
}
